import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../components/Icon';
import CopyButton from '../components/CopyButton';
import { getUsers, getUserDetails, hardDeleteUser } from '../api';
import '../css/admin-delete-user.css';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const PHONE_RE = /^\+?[\d\s()-]{3,}$/;
const PAGE_SIZE = 12;
const SEARCH_DEBOUNCE_MS = 400;

// Строка поиска сама определяет, что ввели: UUID, телефон или имя.
const detectSearchMode = (query) => {
  if (UUID_RE.test(query)) return 'id';
  if (PHONE_RE.test(query)) return 'phone';
  return 'name';
};

const userName = (user) => user?.fullName || user?.username || '';
const userPhone = (user) => user?.defaultPhoneNumber || user?.phoneNumber || '';

const UserAvatar = ({ user, size = 44 }) => {
  const [failed, setFailed] = useState(false);
  const src = user?.avatar || user?.avatarUrl;
  const initial = (userName(user) || '?').trim().charAt(0).toUpperCase();
  return (
    <span className="adu-avatar" style={{ width: size, height: size, fontSize: size * 0.4 }}>
      {src && !failed ? (
        <img src={src} alt="" loading="lazy" decoding="async" onError={() => setFailed(true)} />
      ) : (
        initial
      )}
    </span>
  );
};

const DeleteUserConfirmModal = ({ user, submitting, error, onCancel, onConfirm }) => {
  const { t } = useTranslation();
  const [acknowledged, setAcknowledged] = useState(false);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape' && !submitting) onCancel();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onCancel, submitting]);

  const name = userName(user) || t('admin.deleteUser.noName');

  return createPortal(
    <motion.div
      className="adu-modal-backdrop"
      onClick={submitting ? undefined : onCancel}
      role="presentation"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.18 }}
    >
      <motion.div
        className="adu-modal"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="adu-confirm-title"
        aria-describedby="adu-confirm-text"
        onClick={(e) => e.stopPropagation()}
        initial={{ opacity: 0, y: 16, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 16, scale: 0.97 }}
        transition={{ duration: 0.22, ease: [0.4, 0, 0.2, 1] }}
      >
        <div className="adu-modal-icon">
          <Icon name="alert-triangle" size={26} />
        </div>
        <h3 id="adu-confirm-title" className="adu-modal-title">
          {t('admin.deleteUser.confirm.title')}
        </h3>
        <p id="adu-confirm-text" className="adu-modal-text">
          {t('admin.deleteUser.confirm.subtitle')}
        </p>

        <div className="adu-modal-user">
          <UserAvatar user={user} size={48} />
          <div className="adu-modal-user-texts">
            <span className="adu-modal-user-name">{name}</span>
            <span className="adu-modal-user-meta">
              {[t(`users.roles.${user.role}`, user.role || ''), userPhone(user)].filter(Boolean).join(' · ')}
            </span>
            <code className="adu-modal-user-id">{user.id}</code>
          </div>
        </div>

        <ul className="adu-modal-consequences">
          <li><Icon name="x-circle" size={16} />{t('admin.deleteUser.confirm.consequenceProfile')}</li>
          <li><Icon name="x-circle" size={16} />{t('admin.deleteUser.confirm.consequenceData')}</li>
          <li><Icon name="x-circle" size={16} />{t('admin.deleteUser.confirm.consequenceIrreversible')}</li>
        </ul>

        <label className={`adu-ack${acknowledged ? ' is-checked' : ''}`}>
          <input
            type="checkbox"
            checked={acknowledged}
            onChange={(e) => setAcknowledged(e.target.checked)}
            disabled={submitting}
          />
          <span className="adu-ack-box" aria-hidden="true">
            <Icon name="check" size={14} />
          </span>
          <span>{t('admin.deleteUser.confirm.acknowledge')}</span>
        </label>

        {error && (
          <div className="form-status error adu-modal-error" role="alert">
            {error}
          </div>
        )}

        <div className="adu-modal-actions">
          <button type="button" className="adu-btn adu-btn--ghost" onClick={onCancel} disabled={submitting}>
            {t('admin.deleteUser.confirm.cancel')}
          </button>
          <button
            type="button"
            className="adu-btn adu-btn--danger"
            onClick={onConfirm}
            disabled={!acknowledged || submitting}
          >
            {submitting ? <span className="adu-spinner" aria-hidden="true" /> : <Icon name="trash-2" size={16} />}
            <span>{submitting ? t('admin.deleteUser.deleting') : t('admin.deleteUser.confirm.delete')}</span>
          </button>
        </div>
      </motion.div>
    </motion.div>,
    document.body
  );
};

const AdminDeleteUser = () => {
  const { t } = useTranslation();

  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [searched, setSearched] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [deletedName, setDeletedName] = useState(null);

  const requestIdRef = useRef(0);
  const inputRef = useRef(null);
  const tRef = useRef(t);
  tRef.current = t;

  const runSearch = useCallback(async (rawQuery) => {
    const q = rawQuery.trim();
    const requestId = ++requestIdRef.current;
    if (!q) {
      setResults([]);
      setTotal(0);
      setSearched(false);
      setSearchError(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setSearchError(null);
    try {
      const mode = detectSearchMode(q);
      let users = [];
      let count = 0;
      if (mode === 'id') {
        try {
          const user = await getUserDetails(q);
          users = user ? [{ ...user, id: user.id || q }] : [];
        } catch {
          users = [];
        }
        count = users.length;
      } else {
        const data = await getUsers(
          0,
          PAGE_SIZE,
          'fullName',
          mode === 'name' ? q : '',
          mode === 'phone' ? q.replace(/[\s()-]/g, '') : ''
        );
        users = data?.content || [];
        count = data?.totalElements ?? users.length;
      }
      if (requestId !== requestIdRef.current) return;
      setResults(users);
      setTotal(count);
      setSearched(true);
    } catch (e) {
      if (requestId !== requestIdRef.current) return;
      setSearchError(e?.message || tRef.current('admin.deleteUser.searchError'));
      setResults([]);
      setTotal(0);
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => runSearch(query), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query, runSearch]);

  const closeConfirm = useCallback(() => {
    if (deleteSubmitting) return;
    setDeleteTarget(null);
    setDeleteError(null);
  }, [deleteSubmitting]);

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleteSubmitting(true);
    setDeleteError(null);
    try {
      await hardDeleteUser(deleteTarget.id);
      setResults((prev) => prev.filter((u) => u.id !== deleteTarget.id));
      setTotal((prev) => Math.max(0, prev - 1));
      setDeletedName(userName(deleteTarget) || deleteTarget.id);
      setDeleteTarget(null);
      window.setTimeout(() => setDeletedName(null), 6000);
    } catch (err) {
      setDeleteError(err?.message || t('admin.deleteUser.deleteError'));
    } finally {
      setDeleteSubmitting(false);
    }
  };

  const showEmptyHint = !query.trim() && !loading;
  const showNoResults = searched && !loading && !searchError && results.length === 0 && query.trim();

  return (
    <div className="adu-page">
      <div className="adu-shell">
        <header className="adu-hero">
          <div className="adu-hero-icon">
            <Icon name="trash-2" size={22} />
          </div>
          <div className="adu-hero-texts">
            <h3 className="adu-hero-title">
              {t('admin.deleteUser.title')}
              <span className="adu-dev-chip">
                <Icon name="zap" size={12} />
                DEV
              </span>
            </h3>
            <p className="adu-hero-subtitle">{t('admin.deleteUser.subtitle')}</p>
          </div>
        </header>

        <div className="adu-warning" role="note">
          <Icon name="alert-triangle" size={18} />
          <span>{t('admin.deleteUser.warning')}</span>
        </div>

        <div className="adu-search">
          <div className="adu-search-field">
            <Icon name="search" size={18} className="adu-search-icon" />
            <input
              ref={inputRef}
              type="search"
              className="adu-search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('admin.deleteUser.searchPlaceholder')}
              autoComplete="off"
              spellCheck={false}
              autoFocus
            />
            {loading && <span className="adu-spinner adu-spinner--accent" aria-hidden="true" />}
            {query && !loading && (
              <button
                type="button"
                className="adu-search-clear"
                onClick={() => {
                  setQuery('');
                  inputRef.current?.focus();
                }}
                aria-label={t('admin.deleteUser.clear')}
              >
                <Icon name="x" size={16} />
              </button>
            )}
          </div>
          <div className="adu-search-hints">
            <span><Icon name="user" size={13} />{t('admin.deleteUser.hintName')}</span>
            <span><Icon name="phone" size={13} />{t('admin.deleteUser.hintPhone')}</span>
            <span><Icon name="database" size={13} />{t('admin.deleteUser.hintId')}</span>
          </div>
        </div>

        <AnimatePresence>
          {deletedName && (
            <motion.div
              className="adu-toast"
              role="status"
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
            >
              <Icon name="check-circle" size={18} />
              <span>{t('admin.deleteUser.deleteSuccess', { name: deletedName })}</span>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="adu-body">
          {searchError && (
            <div className="form-status error" role="alert">
              {searchError}
            </div>
          )}

          {showEmptyHint && (
            <div className="adu-empty">
              <span className="adu-empty-icon"><Icon name="search" size={28} /></span>
              <p className="adu-empty-title">{t('admin.deleteUser.emptyTitle')}</p>
              <p className="adu-empty-text">{t('admin.deleteUser.emptyText')}</p>
            </div>
          )}

          {showNoResults && (
            <div className="adu-empty">
              <span className="adu-empty-icon"><Icon name="users" size={28} /></span>
              <p className="adu-empty-title">{t('admin.deleteUser.noResults')}</p>
              <p className="adu-empty-text">{t('admin.deleteUser.noResultsText')}</p>
            </div>
          )}

          {results.length > 0 && (
            <>
              <div className="adu-results-head">
                {t('admin.deleteUser.found', { shown: results.length, total })}
              </div>
              <ul className="adu-results">
                <AnimatePresence initial={false}>
                  {results.map((user) => {
                    const phone = userPhone(user);
                    return (
                      <motion.li
                        key={user.id}
                        className="adu-user"
                        layout
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, x: 40, transition: { duration: 0.2 } }}
                      >
                        <UserAvatar user={user} />
                        <div className="adu-user-main">
                          <div className="adu-user-name-row">
                            <span className="adu-user-name">{userName(user) || t('admin.deleteUser.noName')}</span>
                            {user.role && (
                              <span className={`adu-role adu-role--${String(user.role).toLowerCase()}`}>
                                {t(`users.roles.${user.role}`, user.role)}
                              </span>
                            )}
                            {user.status && user.status !== 'ACTIVE' && (
                              <span className="adu-status">{t(`users.statuses.${user.status}`, user.status)}</span>
                            )}
                          </div>
                          <div className="adu-user-meta">
                            <span><Icon name="phone" size={13} />{phone || '—'}</span>
                            {user.email && <span className="adu-user-email"><Icon name="mail" size={13} />{user.email}</span>}
                          </div>
                          <div className="adu-user-id">
                            <code>{user.id}</code>
                            <CopyButton text={user.id} />
                          </div>
                        </div>
                        <button
                          type="button"
                          className="adu-btn adu-btn--danger-soft"
                          onClick={() => {
                            setDeleteError(null);
                            setDeleteTarget(user);
                          }}
                        >
                          <Icon name="trash-2" size={16} />
                          <span>{t('admin.deleteUser.delete')}</span>
                        </button>
                      </motion.li>
                    );
                  })}
                </AnimatePresence>
              </ul>
              {total > results.length && (
                <p className="adu-results-more">{t('admin.deleteUser.refineHint')}</p>
              )}
            </>
          )}
        </div>
      </div>

      <AnimatePresence>
        {deleteTarget && (
          <DeleteUserConfirmModal
            key={deleteTarget.id}
            user={deleteTarget}
            submitting={deleteSubmitting}
            error={deleteError}
            onCancel={closeConfirm}
            onConfirm={handleConfirmDelete}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminDeleteUser;
